import { graphql } from "gatsby";
import React from "react";
import { useTranslation } from "react-i18next";
import { SwiperSlide } from "swiper/react";

import ArrowRight from "../../assets/icons/arrowRight.svg";
import Breakpoint from "../breakpoint";
import Button from "../button";
import Image from "../image";
import Link from "../link";
import OpeningTeaser from "../location/openingTeaser";
import RichText from "../richText";
import OverviewSlider from "../slider";

// Helper ______________________________________________________________________________________________________________

const SliderCompact: React.FC<{ result: Queries.LocationsSliderFragment }> = ({ result }) => {
  const { title, imageCompactDesktop, imageCompactMobile } = result.data;

  const mobile = <Image image={imageCompactMobile} lazy={false} className="w-full aspect-square" />;
  const desktop = <Image image={imageCompactDesktop} lazy={false} className="w-full aspect-[5/4]" />;

  return (
    <Link link={result} className="flex relative flex-col justify-between mb-0 border-b-0 group animated-image">
      <Breakpoint mobile={mobile} desktop={desktop} breakpoint="md" />
      <div className="flex absolute top-0 items-end w-full h-full location-compact-slider-overlay swiper-slide_absolute">
        <div className="flex justify-between items-center w-full text-white mb-[14px] ml-[8px] mr-[8px] lg:ml-[25px] lg:mr-[21px]">
          <div className="typo-h3">{title?.text}</div>
          <div className="flex justify-center items-center rounded-full border group-hover:scale-105 group-focus:scale-105 base-transition-200 h-[48px] w-[48px] border-white/30 bg-black/10 backdrop-blur-md">
            <ArrowRight height="24" width="24" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const SliderFull: React.FC<{ result: Queries.LocationsSliderFragment }> = ({ result }) => {
  const { t } = useTranslation();

  const { title, street_address: street, zip_code_and_city: city, imageDesktop, imageMobile } = result.data;

  // const plannedOpening = result.data.openingDate ? new Date(result.data.openingDate) < new Date() : false
  const mobile = <Image image={imageMobile} lazy={false} className="w-full aspect-[3/2]" />;
  const desktop = <Image image={imageDesktop} lazy={false} className="w-full aspect-[3/2]" />;

  return (
    <div className="flex flex-col justify-between mb-0 bg-white animated-image grow">
      <Link link={result} className="w-full border-b-0">
        <Breakpoint mobile={mobile} desktop={desktop} breakpoint="md" />
      </Link>
      <div className="flex flex-col justify-between pb-8 p-l-sm-fluid p-r-sm-fluid p-t-md-fluid grow">
        <div>
          <Link link={result} className="border-b-0">
            <span className="block typo-h3">{title?.text}</span>
          </Link>
          <OpeningTeaser result={result.data} />
          {street && city && (
            <a
              target="_blank"
              href={`https://www.google.com/maps/place/${street.replace(" ", "+")}+${city.replace(" ", "+")}`}
              className="block typo-small m-b-sm-fluid text-black/70"
              rel="noreferrer"
            >
              {street} <br /> {city}
            </a>
          )}
        </div>
        <Link link={result} className="text-sm w-fit font-condensed">
          {t("location.details", "Details")}
        </Link>
      </div>
    </div>
  );
};

// Component ___________________________________________________________________________________________________________
type Props = {
  slice:
    | Queries.ContentLocationsSliceFragment
    | Queries.DentistLocationsSliceFragment
    | Queries.HomeLocationsSliceFragment
    | Queries.TreatmentLocationsSliceFragment
    | Queries.TreatmentAddonLocationsSliceFragment
    | Queries.MarketingTreatmentLocationsSliceFragment
    | Queries.LocationLocationsSliceFragment;
};

const Locations: React.FC<Props> = ({ slice }) => {
  const { primary, items } = slice;

  const listOfLocations = items.map((item, index) => {
    const document = {
      uid: "",
      lang: "",
      type: "",
      data: {
        title: { text: null, richText: null },
        street_address: null,
        zip_code_and_city: null,
        imageDesktop: null,
        imageMobile: null,
        openingDate: null,
        openingHours: [],
        imageCompactDesktop: null,
        imageCompactMobile: null,
      },
      ...item.locations?.document,
    };
    if (!document.data || !document.uid) return null;

    switch (primary.layout) {
      case "Compact":
        return (
          <SwiperSlide key={index} virtualIndex={index} className="z-10 !h-auto overflow-hidden rounded-md border-b-0">
            <SliderCompact result={document} />
          </SwiperSlide>
        );
      case "Full":
        return (
          <SwiperSlide
            key={index}
            virtualIndex={index}
            className="z-10 !h-auto min-w-[315px] max-w-[418px] overflow-hidden rounded-md border-b-0 bg-white"
          >
            <SliderFull result={document} />
          </SwiperSlide>
        );
      default:
        throw new Error(`Layout ${primary.layout as string} is not supported`);
    }
  });

  return (
    <section className="mx-auto max-w-screen-max bg-neutral-50">
      <section className="col-span-full gap-0 mx-5 max-w-screen-xl md:mx-11 lg:mx-24 xl:mx-auto p-t-xxxl-fluid baseGrid lg:gap-x-lg">
        <div className="col-span-full lg:col-span-12 m-b-lg-fluid">
          <RichText data={primary.title?.richText} className="block mb-2 font-medium" />
          <RichText data={primary.text?.richText} className="block typo-base text-black/70" />
        </div>
        {primary.buttonText?.richText.length > 0 && (
          <div className="p-b-lg-fluid m-t-lg-fluid col-span-full hidden lg:col-span-12 lg:row-start-auto lg:!mt-0 lg:flex lg:items-end lg:justify-end">
            <Button type="secondary" link={primary.button}>
              <RichText data={primary.buttonText?.richText} />
            </Button>
          </div>
        )}
      </section>
      <section className="col-span-full gap-0 xl:mx-auto p-b-xxxl-fluid baseGrid lg:gap-x-lg">
        {listOfLocations.length > 0 && (
          <OverviewSlider
            slides={listOfLocations}
            type={primary.layout === "Full" ? "locations-full" : "locations-compact"}
            initialSlides={primary.layout === "Full" ? 3 : 2}
          />
        )}
        {primary.buttonText?.richText.length > 0 && (
          <div className="flex col-span-full justify-center items-center lg:hidden m-t-lg-fluid">
            <Button type="secondary" link={primary.button}>
              <RichText data={primary.buttonText?.richText} />
            </Button>
          </div>
        )}
      </section>
    </section>
  );
};

export const query = graphql`
  fragment DentistLocationsSlice on PrismicPageDentistDataBodyLocations {
    ...Slice

    primary {
      title: locations_title {
        ...RichText
      }
      text: locations_text {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      button: button_link {
        ...Link
      }
      layout
    }
    items {
      locations: locations_location {
        ...Link
        document {
          ... on PrismicLocation {
            ...LocationsSlider
          }
        }
      }
    }
  }

  fragment ContentLocationsSlice on PrismicPageContentDataBodyLocations {
    ...Slice

    primary {
      title: locations_title {
        ...RichText
      }
      text: locations_text {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      button: button_link {
        ...Link
      }
      layout
    }
    items {
      locations: locations_location {
        ...Link
        document {
          ... on PrismicLocation {
            ...LocationsSlider
          }
        }
      }
    }
  }

  fragment HomeLocationsSlice on PrismicPageHomeDataBodyLocations {
    ...Slice

    primary {
      title: locations_title {
        ...RichText
      }
      text: locations_text {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      button: button_target {
        ...Link
      }
      layout
    }
    items {
      locations: locations_location {
        ...Link
        document {
          ... on PrismicLocation {
            ...LocationsSlider
          }
        }
      }
    }
  }

  fragment TreatmentLocationsSlice on PrismicTreatmentDataBodyLocations {
    ...Slice

    primary {
      title: locations_title {
        ...RichText
      }
      text: locations_text {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      button: button_target {
        ...Link
      }
      layout
    }
    items {
      locations: locations_location {
        ...Link
        document {
          ... on PrismicLocation {
            ...LocationsSlider
          }
        }
      }
    }
  }

  fragment TreatmentAddonLocationsSlice on PrismicTreatmentAddonDataBodyLocations {
    ...Slice

    primary {
      title: locations_title {
        ...RichText
      }
      text: locations_text {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      button: button_target {
        ...Link
      }
      layout
    }
    items {
      locations: locations_location {
        ...Link
        document {
          ... on PrismicLocation {
            ...LocationsSlider
          }
        }
      }
    }
  }

  fragment MarketingTreatmentLocationsSlice on PrismicMarketingTreatmentDataBodyLocations {
    ...Slice

    primary {
      title: locations_title {
        ...RichText
      }
      text: locations_text {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      button: button_target {
        ...Link
      }
      layout
    }
    items {
      locations: locations_location {
        ...Link
        document {
          ... on PrismicLocation {
            ...LocationsSlider
          }
        }
      }
    }
  }

  fragment LocationLocationsSlice on PrismicLocationDataBodyLocations {
    ...Slice

    primary {
      title: locations_title {
        ...RichText
      }
      text: locations_text {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      button: button_target {
        ...Link
      }
      layout
    }
    items {
      locations: locations_location {
        ...Link
        document {
          ... on PrismicLocation {
            ...LocationsSlider
          }
        }
      }
    }
  }

  fragment LocationsSlider on PrismicLocation {
    ...OpeningTeaser
    uid
    lang
    type
    data {
      title {
        ...RichText
        text
      }
      street_address
      zip_code_and_city
      imageDesktop: image {
        alt
        gatsbyImageData(height: 278, placeholder: BLURRED)
      }
      imageMobile: image {
        alt
        gatsbyImageData(height: 224, placeholder: BLURRED)
      }
      imageCompactDesktop: image {
        alt
        gatsbyImageData(height: 500, placeholder: BLURRED)
      }
      imageCompactMobile: image {
        alt
        gatsbyImageData(height: 336, placeholder: BLURRED)
      }
    }
  }
`;

export default Locations;
